import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function HandoverRequestDialog({ open, onOpenChange, currentShift, onSent }) {
  const [emps, setEmps] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !currentShift) return;
    setSelected(null);
    api
      .get("/employees/available", { params: { exclude: currentShift.employee_id } })
      .then((r) => setEmps(r.data))
      .catch((e) => toast.error(formatError(e)));
  }, [open, currentShift]);

  const send = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/shifts/${currentShift.id}/handover-request`, { next_employee_id: selected });
      toast.success(`Request sent to ${data.shift.pending_next_employee_name}`);
      onOpenChange(false);
      onSent?.(data.shift);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Assign next employee for {currentShift?.mpd}</DialogTitle>
          <DialogDescription>
            The next employee will be notified in-app. They must accept before you complete the handover — fueling continues meanwhile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
          {emps === null && (
            <div className="p-8 grid place-items-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
          )}
          {emps?.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">No other active employees available.</div>}
          {emps?.map((e) => {
            const isSelected = selected === e.id;
            return (
              <button
                key={e.id}
                type="button"
                data-testid={`handover-emp-${e.employee_id}`}
                onClick={() => e.available && setSelected(e.id)}
                disabled={!e.available || submitting}
                className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 transition-colors ${
                  !e.available
                    ? "bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed"
                    : isSelected
                    ? "border-[color:var(--bpcl-blue)] bg-[color:var(--bpcl-blue)]/5 ring-2 ring-[color:var(--bpcl-blue)]/20"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <span className={`w-9 h-9 rounded-full grid place-items-center text-sm font-bold ${
                  e.available ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                }`}>
                  {e.name?.[0]?.toUpperCase() || "?"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{e.name}</div>
                  <div className="text-xs text-slate-500 font-mono-num">{e.employee_id}</div>
                </div>
                <div className="text-right text-xs font-medium">
                  {e.available ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Available
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Running {e.current_mpd}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl" disabled={submitting}>Cancel</Button>
          <Button
            onClick={send}
            disabled={!selected || submitting}
            data-testid="send-handover-request-btn"
            className="rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-2" /> Send handover request</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
